// Rapprochement des opérations (F-049, F-050, D-16) : propositions explicites, toujours validées par l'utilisateur.
import { useState } from "react";
import { post } from "@core/api";
import { useGet } from "@core/hooks";
import { date, money } from "@core/format";
import { Badge, Button, Card, Empty, ErrorBox, Input, Loading, MoneyInput, Notice, Select, SimBadge } from "@core/ui";
import { Amount, Criteria, LevelBadge, SOURCE_LABELS, useAction, useG } from "./shared";

type Proposal = { lease_id: string; lease_label: string; kind: string; level: string; criteria: string[]; allocations: { rent_call_id: string; amount_cents: number }[]; unallocated_cents: number };
type Tx = { id: string; source: string; booked_on: string; amount_cents: number; label: string; payer: string; reference: string; status: string; note: string; proposals?: Proposal[] };
type TxData = { items: Tx[]; leases: { lease_id: string; label: string; tenants: string; reference: string }[]; matching: { amount_tolerance_cents: number; date_window_days: number } };

export function TransactionsToMatch({ status = "a_rapprocher", title = "Opérations à rapprocher" }: { status?: string; title?: string }) {
  const { g, can } = useG();
  const q = useGet<TxData>(g(`/bank-transactions?status=${status}`));
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data!;
  return (
    <Card title={title}>
      <p className="small muted">
        Critères : montant (tolérance {money(d.matching.amount_tolerance_cents)}), nom du payeur, référence de paiement, date à moins de {d.matching.date_window_days} jours d’une échéance. Une proposition n’est jamais validée automatiquement ; un nom de payeur différent n’est pas une anomalie.
      </p>
      {d.items.length === 0 ? (
        <Empty title="Aucune opération en attente">Importez un relevé CSV ou synchronisez votre banque.</Empty>
      ) : (
        <ul className="gestion-list">
          {d.items.map((t) => (
            <TxItem key={t.id} tx={t} leases={d.leases} canWrite={can("finance")} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function TxItem({ tx, leases, canWrite }: { tx: Tx; leases: TxData["leases"]; canWrite: boolean }) {
  const { g } = useG();
  const { busy, run } = useAction();
  const [manual, setManual] = useState(false);
  const [parts, setParts] = useState<{ lease_id: string; amount: number | null; kind: string }[]>([{ lease_id: "", amount: tx.amount_cents, kind: "locataire" }]);
  const [reason, setReason] = useState("");
  const sum = parts.reduce((s, p) => s + (p.amount ?? 0), 0);
  return (
    <li>
      <div className="gestion-line">
        <span>
          <strong>{date(tx.booked_on)}</strong> · {tx.label || "Sans libellé"}
          {tx.payer ? ` · ${tx.payer}` : ""} <Badge>{SOURCE_LABELS[tx.source] ?? tx.source}</Badge>
          {tx.source === "banque" ? <SimBadge mode="simulateur" /> : null}
          {tx.note ? <span className="block small muted">{tx.note}</span> : null}
        </span>
        <Amount cents={tx.amount_cents} strong />
      </div>
      {tx.status === "a_rapprocher" ? (
        <>
          {(tx.proposals ?? []).length === 0 ? <p className="small muted">Aucune correspondance trouvée : choisissez le bail vous-même ou ignorez l’opération.</p> : null}
          {(tx.proposals ?? []).map((p) => (
            <div key={p.lease_id + p.kind} className="gestion-proposal stack-sm">
              <div className="gestion-line">
                <span>
                  <strong>{p.lease_label}</strong> {p.kind === "caf" ? <Badge tone="encre">Part CAF</Badge> : null} <LevelBadge level={p.level} />
                </span>
                {canWrite ? (
                  <Button
                    small
                    busy={busy === p.lease_id}
                    onClick={() =>
                      run(p.lease_id, () => post(g(`/bank-transactions/${tx.id}/reconcile`), { parts: [{ lease_id: p.lease_id, amount_cents: tx.amount_cents, kind: p.kind, allocations: p.allocations }] }), "Rapprochement validé : paiement enregistré.")
                    }
                  >
                    Valider ce rapprochement
                  </Button>
                ) : null}
              </div>
              <Criteria items={p.criteria} />
              {p.unallocated_cents > 0 ? <p className="small">Non affecté (crédit) : {money(p.unallocated_cents)}</p> : null}
            </div>
          ))}
          {canWrite ? (
            <div className="gestion-actions">
              <Button variant="discret" small onClick={() => setManual(!manual)}>
                {manual ? "Masquer la répartition manuelle" : "Choisir le ou les baux"}
              </Button>
              <Input label="Motif pour ignorer" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. : remboursement sans lien avec un loyer" />
              <Button variant="discret" small busy={busy === "ignore"} onClick={() => run("ignore", () => post(g(`/bank-transactions/${tx.id}/ignore`), { reason }), "Opération ignorée.")}>
                Ignorer l’opération
              </Button>
            </div>
          ) : null}
          {manual ? (
            <div className="stack-sm">
              {parts.map((p, i) => (
                <div key={i} className="form-row">
                  <Select
                    label="Bail"
                    value={p.lease_id}
                    onChange={(e) => setParts(parts.map((x, j) => (j === i ? { ...x, lease_id: e.target.value } : x)))}
                    options={[{ value: "", label: "Choisir" }, ...leases.map((l) => ({ value: l.lease_id, label: `${l.label}${l.tenants ? ` — ${l.tenants}` : ""}` }))]}
                  />
                  <MoneyInput label="Montant" cents={p.amount} onChange={(v) => setParts(parts.map((x, j) => (j === i ? { ...x, amount: v } : x)))} />
                  <Select label="Part" value={p.kind} onChange={(e) => setParts(parts.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)))} options={[{ value: "locataire", label: "Locataire" }, { value: "caf", label: "CAF (tiers payant)" }]} />
                </div>
              ))}
              <div className="gestion-actions">
                <Button variant="discret" small onClick={() => setParts([...parts, { lease_id: "", amount: null, kind: "locataire" }])}>
                  Répartir sur un autre bail (virement groupé)
                </Button>
                <Button
                  small
                  disabled={sum !== tx.amount_cents || parts.some((p) => !p.lease_id)}
                  busy={busy === "manual"}
                  onClick={() => run("manual", () => post(g(`/bank-transactions/${tx.id}/reconcile`), { parts: parts.map((p) => ({ lease_id: p.lease_id, amount_cents: p.amount, kind: p.kind })) }), "Rapprochement validé.")}
                >
                  Valider la répartition
                </Button>
              </div>
              {sum !== tx.amount_cents ? <Notice tone="attention">La somme des parts ({money(sum)}) doit être égale au montant de l’opération.</Notice> : null}
            </div>
          ) : null}
        </>
      ) : null}
      {(tx.status === "ignore" || tx.status === "doublon") && canWrite ? (
        <Button variant="discret" small busy={busy === "reopen"} onClick={() => run("reopen", () => post(g(`/bank-transactions/${tx.id}/reopen`)), "Opération remise à rapprocher.")}>
          Remettre à rapprocher
        </Button>
      ) : null}
    </li>
  );
}
